import { useEffect, useLayoutEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

const STORAGE_PREFIX = 'scroll:';
const ANCHOR_SUFFIX = '.anchor';
/** Genug Frames, bis Tabellenzeilen nach Daten-Refetch das volle Layout haben */
const RESTORE_MAX_FRAMES = 30;
const POST_MOUNT_GUARD_MS = 800;

/**
 * Scrollposition + optionaler Anker (z. B. geöffnete Bestellnummer) für Listenansichten.
 *
 * - Bei Mount mit history.action !== POP werden gespeicherte Werte verworfen (frischer
 *   Sprung ins Menü), damit keine veraltete Position wiederhergestellt wird.
 * - ScrollToTop in App.tsx mountet NACH Shell, damit bei PUSH zuerst diese Listener
 *   die echte scrollY sichern, bevor die Seite nach oben springt.
 */
export function useScrollRestoration(key: string, isReady = true): void {
    const history = useHistory();
    const storageKey = STORAGE_PREFIX + key;
    const anchorKey = storageKey + ANCHOR_SUFFIX;
    /** Nur beim ersten Render auswerten — später kann action wieder PUSH sein */
    const initialActionRef = useRef(history.action);
    const shouldRestoreRef = useRef(initialActionRef.current === 'POP');
    const targetYRef = useRef<number | null>(null);
    const restoredRef = useRef(false);
    const mountedAtRef = useRef(Date.now());
    const frozenRef = useRef(false);

    useLayoutEffect(() => {
        mountedAtRef.current = Date.now();
        frozenRef.current = false;
        restoredRef.current = false;

        if (!shouldRestoreRef.current) {
            try {
                sessionStorage.removeItem(storageKey);
                sessionStorage.removeItem(anchorKey);
            } catch {
                // ignorieren
            }
            targetYRef.current = null;
            return;
        }

        try {
            const saved = sessionStorage.getItem(storageKey);
            if (saved !== null) {
                const y = parseInt(saved, 10);
                if (!Number.isNaN(y) && y >= 0) {
                    targetYRef.current = y;
                }
            }
        } catch {
            // ignorieren
        }
    }, [storageKey, anchorKey]);

    useLayoutEffect(() => {
        if (!shouldRestoreRef.current || !isReady) {
            return;
        }

        let cancelled = false;
        let frame = 0;

        const tick = (): void => {
            if (cancelled) {
                return;
            }

            let anchorId: string | null = null;
            try {
                anchorId = sessionStorage.getItem(anchorKey);
            } catch {
                anchorId = null;
            }

            if (anchorId) {
                const el = document.querySelector<HTMLElement>(`[data-cy="bestelldetails-link-${anchorId}"]`);
                if (el) {
                    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                    restoredRef.current = true;
                    targetYRef.current = null;
                    return;
                }
            }

            const target = targetYRef.current;
            const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
            if (target != null) {
                const desired = Math.min(target, maxScroll);
                if (Math.abs(window.scrollY - desired) > 1) {
                    window.scrollTo({ top: desired, left: 0, behavior: 'auto' });
                }
            }

            const targetVal = targetYRef.current;
            const yOk = targetVal == null || Math.abs(window.scrollY - Math.min(targetVal, maxScroll)) <= 2 || maxScroll >= targetVal - 1;
            const anchorOk = !anchorId || document.querySelector(`[data-cy="bestelldetails-link-${anchorId}"]`);

            if ((yOk && anchorOk) || frame >= RESTORE_MAX_FRAMES) {
                restoredRef.current = true;
                targetYRef.current = null;
                return;
            }

            frame += 1;
            window.requestAnimationFrame(tick);
        };

        window.requestAnimationFrame(tick);
        return (): void => {
            cancelled = true;
        };
    }, [storageKey, anchorKey, isReady]);

    useEffect(() => {
        const writeY = (y: number): void => {
            try {
                sessionStorage.setItem(storageKey, String(y));
            } catch {
                // ignorieren
            }
        };

        const saveScroll = (): void => {
            if (frozenRef.current) {
                return;
            }
            if (
                shouldRestoreRef.current &&
                !restoredRef.current &&
                targetYRef.current != null &&
                Date.now() - mountedAtRef.current < POST_MOUNT_GUARD_MS
            ) {
                return;
            }
            writeY(window.scrollY);
        };

        const saveScrollBeforeNavigate = (): void => {
            writeY(window.scrollY);
            frozenRef.current = true;
        };

        let scrollTick = 0;
        const onScroll = (): void => {
            if (scrollTick) {
                return;
            }
            scrollTick = window.requestAnimationFrame(() => {
                scrollTick = 0;
                saveScroll();
            });
        };

        const unlistenHistory = history.listen((_loc, action) => {
            if (action === 'PUSH' || action === 'REPLACE') {
                writeY(window.scrollY);
                frozenRef.current = true;
            }
        });

        window.addEventListener('scroll', onScroll, { passive: true });
        document.addEventListener('click', saveScrollBeforeNavigate, true);
        document.addEventListener('pointerdown', saveScrollBeforeNavigate, true);
        document.addEventListener('keydown', saveScrollBeforeNavigate, true);
        window.addEventListener('beforeunload', saveScroll);
        return (): void => {
            window.removeEventListener('scroll', onScroll);
            document.removeEventListener('click', saveScrollBeforeNavigate, true);
            document.removeEventListener('pointerdown', saveScrollBeforeNavigate, true);
            document.removeEventListener('keydown', saveScrollBeforeNavigate, true);
            window.removeEventListener('beforeunload', saveScroll);
            unlistenHistory();
        };
    }, [history, storageKey]);
}
